import { useMemo } from 'react';
import * as Yup from 'yup';

import {
  calculateManualPaymentAmounts,
  type ResidentBalanceInfo,
} from '@pflegenavi/shared/api';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import type { FamilyMemberResponse } from '@pflegenavi/frontend/family-member-context';
import { useSetupDirectDebitValidationSchema } from '@pflegenavi/shared/frontend';

export interface IManualPaymentFormValues {
  amount: number;
  balance: number;
  name: string;
  email: string;
  iban: string;
}

interface UseManualPaymentValidationSchemaProps {
  familyMember: FamilyMemberResponse | undefined;
  balanceInfo: ResidentBalanceInfo;
  residentBalance: number | undefined;
}

export const useManualPaymentValidationSchema = ({
  balanceInfo,
  familyMember,
  residentBalance,
}: UseManualPaymentValidationSchemaProps): {
  initialValues: IManualPaymentFormValues;
  validationSchema: Yup.ObjectSchema<any>;
} => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const paymentAmounts = calculateManualPaymentAmounts(
    balanceInfo,
    residentBalance
  );

  const minAmount = paymentAmounts.minAmountCents;
  const maxAmount = paymentAmounts.maxAmountCents;
  const initialAmount = paymentAmounts.initialAmountCents;

  const initialValues: IManualPaymentFormValues = useMemo(() => {
    if (familyMember) {
      return {
        name: `${familyMember.given_name} ${familyMember.family_name}`,
        email: familyMember.email,
        amount: initialAmount,
        balance: residentBalance ? residentBalance : 0,
        iban: '',
      };
    }
    return {
      name: '',
      email: '',
      amount: initialAmount,
      balance: 0,
      iban: '',
    };
  }, [familyMember, initialAmount, residentBalance]);

  const validationSchema = Yup.object(
    useSetupDirectDebitValidationSchema()
  ).concat(
    Yup.object().shape({
      amount: Yup.number()
        .required(
          t('fm-triggered-payments.amount-screen.error-required-amount')
        )
        .min(
          minAmount,
          t('fm-triggered-payments.amount-screen.error-minimum-amount', {
            amount: fCurrencyCents(minAmount),
          })
        )
        .max(
          maxAmount,
          t('fm-triggered-payments.amount-screen.error-maximum-amount', {
            amount: fCurrencyCents(maxAmount),
          })
        ),
    })
  );

  return {
    initialValues,
    validationSchema,
  };
};
